import { get, set, unset } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "src/contexts/user-context";
import { useUpdateWorkspacePersonalizationMutation } from "src/graphql";
import { captureException } from "src/lib/monitoring";
import { isAdminWithWritePrivilege } from "src/utils/user";
import store from "store2";

const LOCAL_PERSONALIZATION_CHANGE_EVENT =
  "hightouch:local-personalization-change";

// Keys in our personalization system
type PersonalizationKey =
  | "home.chart"
  | "charts.fast-query"
  | "charts.launch-banner-dismissed"
  | "audience-overview.launch-banner-dismissed"
  | "journeys.coupon-announcement-banner-dismissed"
  | "syncs.cant-find-column-dismissed"
  | "schema.tree-sidebar-banner-dismissed"
  | "agents.audiences-agent-announcement-dialog-dismissed"
  | "agents.tos-notice-dismissed"
  | "agents.tos-modal-dismissed"
  | "agents.global-tos-modal-dismissed"
  | "agents.ai-features-announcement-dismissed"
  | "cep.device-previews.filters"
  | "home.agentic-homepage-announcement-dismissed"
  | "lcm.intro-modal-dismissed";

export interface PersonalizationOptions<T> {
  /**
   * The key to use for storing the value in local storage or workspace personalization
   * Can be a nested path like "home.chart" which will be handled by lodash get/set
   */
  key: PersonalizationKey;

  /**
   * The initial value to use if no value is found in storage
   */
  initialValue: T;

  /**
   * Whether to use workspace personalization (if available) or always use local storage
   * @default true
   */
  useWorkspace?: boolean;
}

export interface PersonalizationValue<T> {
  /**
   * The current value
   */
  value: T;

  /**
   * Whether the current value is from workspace personalization
   */
  isWorkspaceValue: boolean;

  /**
   * Whether the value is being saved to the workspace
   */
  isSaving: boolean;

  /**
   * Set the value in local storage
   */
  setLocal: (value: T) => void;

  /**
   * Set the value in workspace personalization (if available and enabled)
   */
  save: (value: T) => void;

  /**
   * Clear the value completely
   */
  clear: () => void;

  /**
   * Clear the value from workspace personalization
   */
  clearWorkspacePersonalization: () => void;
}

type LocalPersonalizationChangeDetail = {
  localStorageKey: string;
  value: unknown;
};

const dispatchLocalPersonalizationChange = <T>(
  localStorageKey: string,
  value: T,
) => {
  window.dispatchEvent(
    new CustomEvent<LocalPersonalizationChangeDetail>(
      LOCAL_PERSONALIZATION_CHANGE_EVENT,
      {
        detail: { localStorageKey, value },
      },
    ),
  );
};

export const usePersonalization = <T>({
  key,
  initialValue,
  useWorkspace = true,
}: PersonalizationOptions<T>): PersonalizationValue<T> => {
  const { workspace, user } = useUser();
  const localStorageKey = `${workspace?.id}:personalization:${key}`;
  const updateWorkspaceMutation = useUpdateWorkspacePersonalizationMutation({
    onSuccess: () => {
      // Don't reload queries
    },
  });
  // Used for optimistic update
  const [workspaceValue, setWorkspaceValue] = useState<T | undefined>(
    get(workspace?.personalization, key),
  );
  const [value, setValue] = useState<T>(
    store.get(localStorageKey) ?? initialValue,
  );

  // Update the value when workspace personalization changes
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    const localValue = store.get(localStorageKey) ?? initialValue;
    const workspaceValue = get(workspace?.personalization, key);
    if (useWorkspace && workspaceValue !== undefined) {
      setWorkspaceValue(workspaceValue);
      setValue(workspaceValue);
    } else {
      setValue(localValue);
      setWorkspaceValue(undefined);
    }
  }, [useWorkspace, workspace?.personalization, key]);

  useEffect(() => {
    const onLocalPersonalizationChange = (event: Event) => {
      const detail = (event as CustomEvent<LocalPersonalizationChangeDetail>)
        .detail;
      if (detail.localStorageKey !== localStorageKey) return;

      setValue(detail.value as T);
      if (!useWorkspace) {
        setWorkspaceValue(undefined);
      }
    };

    window.addEventListener(
      LOCAL_PERSONALIZATION_CHANGE_EVENT,
      onLocalPersonalizationChange,
    );

    return () => {
      window.removeEventListener(
        LOCAL_PERSONALIZATION_CHANGE_EVENT,
        onLocalPersonalizationChange,
      );
    };
  }, [localStorageKey, useWorkspace]);

  // Set value in local storage
  const setLocal = useCallback(
    (newValue: T) => {
      setValue(newValue);
      if (newValue !== undefined) {
        store.set(localStorageKey, newValue);
      }
      dispatchLocalPersonalizationChange(localStorageKey, newValue);
    },
    [localStorageKey],
  );

  // Set value in workspace personalization
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const save = useCallback(
    async (newValue: T) => {
      // Update state immediately
      setValue(newValue);
      setWorkspaceValue(newValue);
      const updatedPersonalization = {
        ...(workspace!.personalization || {}),
      };
      set(updatedPersonalization, key, newValue);
      try {
        await updateWorkspaceMutation.mutateAsync({
          workspaceId: workspace!.id,
          personalization: updatedPersonalization,
        });
      } catch (e) {
        captureException(e);
      }
    },
    [workspace, key, localStorageKey, updateWorkspaceMutation],
  );

  const clear = useCallback(async () => {
    setValue(initialValue);
    setWorkspaceValue(undefined);
    store.remove(localStorageKey);
    dispatchLocalPersonalizationChange(localStorageKey, initialValue);

    if (useWorkspace && isAdminWithWritePrivilege(user?.admin_type)) {
      const updatedPersonalization = {
        ...(workspace!.personalization ?? {}),
      };
      unset(updatedPersonalization, key);
      try {
        await updateWorkspaceMutation.mutateAsync({
          workspaceId: workspace!.id,
          personalization: updatedPersonalization,
        });
      } catch (e) {
        captureException(e);
      }
    }
  }, [
    useWorkspace,
    user,
    workspace,
    key,
    localStorageKey,
    initialValue,
    updateWorkspaceMutation,
  ]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const clearWorkspacePersonalization = useCallback(async () => {
    if (useWorkspace && isAdminWithWritePrivilege(user?.admin_type)) {
      setWorkspaceValue(undefined);
      const updatedPersonalization = {
        ...(workspace!.personalization ?? {}),
      };
      unset(updatedPersonalization, key);
      try {
        await updateWorkspaceMutation.mutateAsync({
          workspaceId: workspace!.id,
          personalization: updatedPersonalization,
        });
      } catch (e) {
        captureException(e);
      }
    }
  }, [useWorkspace, user]);

  const isWorkspaceValue = useMemo(() => {
    if (!useWorkspace) return false;
    return workspaceValue !== undefined && value === workspaceValue;
  }, [useWorkspace, workspaceValue, value]);

  return {
    value,
    isWorkspaceValue,
    isSaving: updateWorkspaceMutation.isLoading,
    setLocal,
    save,
    clear,
    clearWorkspacePersonalization,
  };
};
